<template lang="pug">
  div(
    class="dynamic-switch"
    :class="switchClasses"
    @keydown="onKeyDown"
  )
    button(
      class="switch-option"
      :class="yesOptionClasses"
      :disabled="disabled || canEdit"
      @click="select('yes')"
      :style="optionStyles"
    ) {{ yesLabel }}
    button(
      class="switch-option"
      :class="noOptionClasses"
      :disabled="disabled || canEdit"
      :style="optionStyles"
      @click="select('no')"
    ) {{ noLabel }}

    input(
      type="hidden"
      :value="currentValue"
    )
</template>

<script>
import responsive from '~/mixins/pages/responsive'

export default {
  name: 'TemplatesDynamicSwitch',
  mixins: [responsive],
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: String,
      default: null
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    isResizing: {
      type: Boolean,
      default: false
    },
    fontSize: {
      type: [String, Number],
      default: null
    },
    hasError: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentValue: this.value
    }
  },
  computed: {
    disabled() {
      return this.field.documentFieldPreValue || this.isDisabled
    },
    switchClasses() {
      return {
        disabled: this.disabled,
        'error-state': this.hasError && !this.currentValue
      }
    },
    yesOptionClasses() {
      return {
        selected: this.currentValue === 'yes',
        'disabled-option': this.disabled || this.canEdit,
        'error-state': this.hasError && !this.currentValue
      }
    },
    noOptionClasses() {
      return {
        selected: this.currentValue === 'no',
        'disabled-option': this.disabled || this.canEdit,
        'error-state': this.hasError && !this.currentValue
      }
    },
    labels() {
      const raw = this.field.hint
      return raw.split(/[/|]/).map(p => p.trim())
    },
    yesLabel() {
      return this.labels[0]
    },
    noLabel() {
      return this.labels[1]
    },
    optionStyles() {
      return {
        fontSize: this.xsDevice ? '7px' : this.fontSize + 'px'
      }
    }
  },
  watch: {
    value(val) {
      this.currentValue = val
    },
    currentValue(val) {
      this.$emit('update:value', val)
    }
  },
  created() {
    this.initFromBackend()
  },
  methods: {
    initFromBackend() {
      if (!this.canEdit) {
        const pre = this.field.documentFieldPreValue
        const real = this.field.documentFieldValue
        this.currentValue = pre || real || null
        if (this.currentValue) {
          this.$emit('update:value', this.currentValue)
        }
      }
    },
    select(val) {
      if (this.disabled) return
      this.currentValue = this.currentValue === val ? null : val
    },
    onKeyDown(e) {
      if (e.ctrlKey && e.key === 'c') this.$emit('copy')
      if (e.ctrlKey && e.key === 'x') this.$emit('cut')
      if (e.key === 'Delete') this.$emit('delete')
    }
  }
}
</script>

<style scoped lang="scss">
.dynamic-switch {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);

  border: 1px solid $primary;
  border-radius: 0.667em;
  overflow: hidden;
  @media (max-width: map-get($breakpoints, 'sm')) {
    width: 100%;
    border-radius: 0.333em;
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &.error-state {
    border: 1px solid $danger;
    background: rgba(255, 23, 68, 0.12);
  }

  .switch-option {
    padding: 0.167em 0.667em;
    background: $primary-light;
    width: 100%;
    height: 100%;
    color: $primary;
    font-size: 12px;
    font-family: inherit;
    text-align: center;
    font-weight: 300;
    cursor: pointer;
    transition:
      background-color 0.3s ease,
      color 0.3s ease;

    &.disabled-option {
      background: none;
      color: $text-dark;
      pointer-events: none;
    }

    @media (max-width: map-get($breakpoints, 'sm')) {
      padding: 0 0.25em;
      font-size: 0.583em;
      width: 50%;
    }

    &.selected {
      background: $primary;
      color: #fff;
    }

    &.error-state {
      background: rgba(255, 23, 68, 0.12);
      color: $dark2;
    }
  }
}
.switch-option:first-child {
  border-right: 1px solid $primary;
  &.disabled-option {
    border-right: 1px solid #d4d4d4;
  }
  &.error-state {
    border-right: 1px solid $danger;
  }
}
</style>
